import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import {
    FaShieldAlt,        // For Zero Depreciation
    FaTools,            // For Engine Protection
    FaTruckPickup,      // For 24x7 Roadside Assistance
    FaOilCan,           // For Consumables Cover
    FaFileInvoice,      // For Return to Invoice
    FaCloudSunRain,
    FaUserLock,
    FaCarCrash,
    FaUserShield,
    FaCarSide,       // For Peace of mind
} from "react-icons/fa";
import {
    Scooter,
    Bike,
    Sparkles,
    Mountain,
    Flame,
  } from "lucide-react";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";
import { GiScooter } from "react-icons/gi";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const SuzukiTwoWheelerInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is insurance compulsory for my Suzuki scooter or bike?",
      answer: "Yes, third-party insurance is mandatory by law in India.",
    },
    {
      question: "What is the difference between third-party and comprehensive insurance?",
      answer: "Third-party covers damage to others only. Comprehensive covers your own damage, theft, natural disasters, and more.",
    },
    {
      question: "What type of insurance is best for Suzuki Access or Burgman?",
      answer: "A comprehensive plan with zero depreciation and engine protection is ideal.",
    },
    {
      question: "Can I buy insurance online for my Suzuki Gixxer?",
      answer: "Yes, most insurers offer paperless online insurance purchases with instant policy issuance.",
    },
    {
      question: "Which Suzuki model is the cheapest to insure?",
      answer: "Suzuki Access 125 usually has the lowest insurance premium due to its low IDV and maintenance cost.",
    },
    {
      question: "Do electric Suzuki models have separate insurance?",
      answer: "As of now, Suzuki India doesn’t have EVs. When they do, separate EV policies will apply.",
    },
    {
      question: "Does comprehensive insurance cover helmet or gear loss?",
      answer: "No, but some insurers offer add-ons for riding gear protection.",
    },
    {
      question: "Is personal accident cover included in all Suzuki policies?",
      answer: "Yes, owner-driver accident cover is mandatory, but pillion rider cover is optional.",
    },
    {
      question: "What is IDV and how does it affect Suzuki bike insurance?",
      answer: "Insured Declared Value is the current market value of your bike. A higher IDV increases your premium but gives better coverage.",
    },
    {
      question: "Can I insure my modified Suzuki Gixxer?",
      answer: "Yes, but modifications must be declared. It may affect premium pricing.",
    },
    {
      question: "Do I need to get my Suzuki bike inspected before insurance renewal?",
      answer: "Only if there has been a policy lapse or major claims in the past.",
    },
    {
      question: "What is NCB in Suzuki insurance?",
      answer: "No Claim Bonus is a discount on your premium for every claim-free year. It can go up to 50%.",
    },
    {
      question: "Can I transfer my Suzuki insurance to a new owner?",
      answer: "Yes, insurance can be transferred when you sell your bike, but it requires a formal request and updated documents.",
    },
    {
      question: "How long does Suzuki insurance claim settlement take?",
      answer: "Usually 5 to 7 business days, depending on the insurer and claim type.",
    },
    {
      question: "Are cashless garages available for Suzuki scooters?",
      answer: "Yes, most insurers have a tie-up with authorized Suzuki service centers.",
    },
    {
      question: "Do Suzuki scooters qualify for engine protection coverage?",
      answer: "Yes, especially in cities with heavy monsoon and flood risk.",
    },
    {
      question: "Is roadside assistance worth it for Suzuki bikes?",
      answer: "Absolutely, especially if you travel frequently or use your bike for long-distance commutes.",
    },
    {
      question: "Can I port my insurance to another company during renewal?",
      answer: "Yes, and your NCB benefit can be carried over too.",
    },
    {
      question: "What happens if I ride my Suzuki bike without insurance?",
      answer: "You can be fined, and any damage costs will be entirely out-of-pocket.",
    },
    {
      question: "Does TheFinMaster help with Suzuki bike insurance claims?",
      answer: "Yes, we assist users in understanding policies, comparing insurers, and initiating claims smoothly.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation",
      description: "Ideal for Gixxer and Burgman riders to avoid cost deductions during part replacements.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine Protection",
      description: "Especially useful for scooters in flood-prone cities.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "24x7 Roadside Assistance",
      description: "Crucial during long rides or sudden breakdowns.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Return to Invoice",
      description: "Best suited for new purchases to recover full invoice value.",
      icon: <FaFileInvoice className="text-4xl text-purple-600 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description: "Covers screws, oils, nuts, and small parts that are otherwise excluded.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
  ];

  const features = [
    { icon: '🛡️', text: 'Reliable & Accurate Estimates' },
    { icon: '⏱️', text: 'Takes Less Than a Minute' },
    { icon: '📉', text: 'Instantly Shows Premium Breakup' },
    { icon: '📄', text: 'No Paperwork or Documentation' },
    { icon: '📲', text: 'Easy to Use on Any Device' },
    { icon: '💰', text: 'Helps You Save on Insurance' },
  ];

  const benefits = [
    {
      title: "Suzuki Access 125",
      desc: "One of India’s best-selling 125cc scooters.",
      icon: <GiScooter className="w-7 h-7 text-rose-500" />,
      gradient: "from-rose-50 to-rose-100"
    },
    {
      title: "Suzuki Burgman Street",
      desc: " Premium maxi-scooter with unique styling",
      icon: <Sparkles className="w-7 h-7 text-amber-500" />,
      gradient: "from-amber-50 to-amber-100"
    },
   
    {
      title: "Suzuki Avenis ",
      desc: "Youth-centric sporty scooter",
      icon:  <Flame className="w-7 h-7 text-green-500" />,
      gradient: "from-green-50 to-green-100"
    },
    {
      title: "Suzuki V-Strom SX ",
      desc: "Adventure bike for long-distance touring",
      icon:  <Mountain className="w-7 h-7 text-purple-500" />,
      gradient: "from-purple-50 to-purple-100"
    },
    {
        title: "Suzuki Gixxer / Gixxer SF",
        desc: "Performance-focused street bikes and faired sports bikes",
        icon:  <Bike className="w-7 h-7 text-blue-500" />,
        gradient: "from-blue-50 to-blue-100"
      },
  ];
//   const models = [
//     {
//       name: "Suzuki Access 125",
//       desc: "One of India’s best-selling 125cc scooters",
//       icon: <Bike className="w-6 h-6 text-rose-500" />, // soft red like 'road accident' icon
//     },
//     {
//       name: "Suzuki Burgman Street",
//       desc: "Premium maxi-scooter with unique styling",
//       icon: <Sparkles className="w-6 h-6 text-amber-500" />, // warm amber like 'theft' icon
//     },
//     {
//       name: "Suzuki Gixxer / Gixxer SF",
//       desc: "Performance-focused street and faired bikes",
//       icon: <TrendingUp className="w-6 h-6 text-blue-500" />, // blue like 'fire/natural calamities'
//     },
//     {
//       name: "Suzuki Avenis",
//       desc: "Youth-centric sporty scooter",
//       icon: <Star className="w-6 h-6 text-green-500" />, // green like 'third-party liability'
//     },
//     {
//       name: "Suzuki V-Strom SX",
//       desc: "Adventure bike for long-distance touring",
//       icon: <Globe2 className="w-6 h-6 text-purple-500" />, // purple like 'expensive repairs'
//     },
//   ];



const suzukiinsurance = [
    {
      name: "Suzuki Access125",
      path: "/suzuki-access-125-bike-insurance", 
      image: "/assets/suzukiaccess125.jpeg",
    },
    {
      name: "Burgman Street125",
      path: "/suzuki-burgman-street125-bike-insurance", 
      image: "/assets/suzukiburgmanstreet125.jpeg",
    },
    {
      name: "Gixxer",
      path: "/suzuki-gixxer-bike-insurance", 
      image: "/assets/suzukigixxer.jpeg",
    },
     {
      name: "VStrom SX250",
      path: "/suzuki-vstrom-sx250-bike-insurance", 
      image: "/assets/suzukivstromsx250.jpeg",
    },
 {
      name: "VStrom 800DE",
      path: "/suzuki-vstrom-800de-bike-insurance", 
      image: "/assets/suzukivstrom800de.jpeg",
    },
    {
      name: "Hayabusa",
      path: "/suzuki-hayabusa-bike-insurance", 
      image: "/assets/suzukihayabusa.jpeg",
    },
    {
      name: "Avenis125",
      path: "/suzuki-avenis125-bike-insurance", 
      image: "/assets/suzukiavenis125.jpeg",
    },
  ];
  return (
    <>
      <Helmet>
        <title>Suzuki Two-Wheeler Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore Suzuki two-wheeler insurance options in India. Compare quotes, customize coverage, and buy online to protect your Suzuki bike or scooter."
        />
        <meta
          name="keywords"
          content="suzuki two-wheeler insurance, bike insurance india, suzuki access insurance, suzuki gixxer insurance, suzuki burgman insurance, two-wheeler insurance quotes"
        />

          <link rel="canonical" href="https://thefinmaster.com/suzuki-two-wheeler-insurance" />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
        {/* <link rel="canonical" href="https://thefinmaster.com/suzuki-two-wheeler-insurance" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard, max-video-preview:-1" />
        <meta property="og:title" content="Suzuki Two-Wheeler Insurance in India" />
        <meta property="og:description" content="Compare and buy Suzuki two-wheeler insurance online with ease." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thefinmaster.com/suzuki-two-wheeler-insurance" />
        <meta name="twitter:card" content="summary_large_image" /> */}
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Suzuki Two-Wheeler Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">Suzuki Two-Wheeler Insurance </span> 
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Suzuki offers a perfect mix of style, performance, and comfort across its range of two-wheelers. Whether you ride a practical Access scooter or a sporty Gixxer, safeguarding your bike starts with the right insurance coverage. This guide walks you through everything you need to know about insuring a Suzuki bike, from average premiums and model insights to useful add-ons, renewal tips, and frequently asked questions.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/suzukiinsurance.jpeg"
                alt="Suzuki Two-Wheeler"
              />
            </div>
          </div>
        </div>
      </div>
      {/* <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Suzuki in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Suzuki two-wheelers are known for their refinement, smooth engines, and solid build quality. The brand caters to both scooter buyers and motorcycle enthusiasts with popular models like:
          </p>
          <ul className="text-gray-600 leading-relaxed mt-3 list-disc list-inside">
            <li>Suzuki Access 125 – One of India’s best-selling 125cc scooters</li>
            <li>Suzuki Burgman Street – Premium maxi-scooter with unique styling</li>
            <li>Suzuki Gixxer / Gixxer SF – Performance-focused street bikes and faired sports bikes</li>
            <li>Suzuki Avenis – Youth-centric sporty scooter</li>
            <li>Suzuki V-Strom SX – Adventure bike for long-distance touring</li>
          </ul>
          <p className="text-gray-600 leading-relaxed mt-3">
            Suzuki models appeal to daily commuters, style-conscious youth, and even touring enthusiasts.
          </p>
        </div>
      </div> */}
       <section className="bg-gradient-to-r from-blue-50 via-white to-blue-100 py-16 px-6 sm:px-12 lg:px-20 rounded-3xl shadow-lg max-w-7xl mx-auto mt-12">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-slate-800">
        About Suzuki in India
        </h2>
        <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
        Suzuki two-wheelers are known for their refinement, smooth engines, and solid build quality. The brand caters to both scooter buyers and motorcycle enthusiasts with popular models like:        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {benefits.map((item, index) => (
          <div
            key={index}
            className={`bg-gradient-to-br ${item.gradient} rounded-2xl p-5 shadow hover:shadow-md hover:scale-[1.02] transition-transform`}
          >
            <div className="flex items-center justify-center bg-white w-14 h-14 rounded-full shadow mb-4 mx-auto">
              {item.icon}
            </div>
            <h3 className="text-lg font-semibold text-slate-800 text-center mb-2">
              {item.title}
            </h3>
            <p className="text-sm text-slate-600 text-center leading-relaxed">
              {item.desc}
            </p>
          </div>
        ))}
      </div>

      {/* Highlight Banner */}
      <div className="mt-12 p-5 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-white text-center shadow-sm">
        <p className="text-blue-700 text-sm font-medium">
        Suzuki models appeal to daily commuters, style-conscious youth, and even touring enthusiasts.        </p>
      </div>
    </section>

    <div className="p-4 sm:p-8 bg-white">
                                                                        <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                                                                          Choose Your Bike Brand
                                                                        </h2>
                                                                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                                                          {suzukiinsurance.map((calc, index) => (
                                                                            <motion.div
                                                                              key={index}
                                                                              whileHover={{ scale: 1.04 }}
                                                                              initial={{ opacity: 0, y: 20 }}
                                                                              animate={{ opacity: 1, y: 0 }}
                                                                              transition={{ duration: 0.3, delay: index * 0.05 }}
                                                                            >
                                                                              <Link
                                                                                to={calc.path}
                                                                                className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                                                                              >
                                                                                <img
                                                                                  src={calc.image}
                                                                                  alt={calc.name}
                                                                                  className="w-16 h-16 object-contain mb-2"
                                                                                />
                                                                                <span className="text-md font-medium text-gray-800 break-words leading-snug">
                                                                                  {calc.name}
                                                                                </span>
                                                                              </Link>
                                                                            </motion.div>
                                                                          ))}
                                                                        </div>
                                                                      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Suzuki Bikes & Scooters Need Insurance
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Regardless of segment or model, insurance is critical for any Suzuki vehicle to ensure:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Coverage against accidents and repairs</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Compensation in case of theft or total loss</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Financial protection against natural or man-made disasters</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Compliance with the law via third-party liability cover</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserShield className="text-xl text-indigo-500 mt-1" />
              <span>Peace of mind during breakdowns, mishaps, or emergencies</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            A comprehensive insurance policy ensures your Suzuki stays protected and roadworthy, offering financial security and legal compliance.
          </div>
        </motion.div>
      </section>
          <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
             <div className="flex flex-col md:flex-row justify-between items-center gap-10">
               <div className="max-w-xl space-y-6 text-center md:text-left">
                 <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
                   Calculate Your TwoWheeler Insurance <br />
                   in <span className="text-blue-600">Seconds</span>
                 </h2>
                 <p className="text-gray-700 text-lg">
                   Use our fast and free calculator to estimate your premium — no paperwork, no waiting.
                 </p>
                 <Link
                   to="/financial-tools-calculators/bike-insurance-calculator"
                   className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
                 >
                   Start Calculating <ArrowRight className="w-5 h-5" />
                 </Link>
               </div>
               <div className="space-y-4 text-left">
                 {features.map((item, index) => (
                   <div key={index} className="flex items-center gap-3">
                     <span className="text-xl" role="img" aria-label={item.text}>{item.icon}</span>
                     <span className="text-gray-800 font-medium">{item.text}</span>
                   </div>
                 ))}
               </div>
             </div>
           </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Suzuki Models and Estimated Insurance Premiums
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Below are the estimated insured declared values (IDV) and starting insurance premiums for key Suzuki models:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { model: "Suzuki Access 125", category: "125cc Scooter", idv: "75,000 – 90,000", premium: "₹850 – ₹1,200" },
              { model: "Suzuki Burgman Street", category: "Premium Maxi Scooter", idv: "95,000 – 1.10 Lakh", premium: "₹1,100 – ₹1,400" },
              { model: "Suzuki Gixxer 155", category: "Street Sport Motorcycle", idv: "1.1 – 1.3 Lakh", premium: "₹1,300 – ₹1,700" },
              { model: "Suzuki Gixxer SF 250", category: "Full-Faired Performance", idv: "1.6 – 1.8 Lakh", premium: "₹1,800 – ₹2,200" },
              { model: "Suzuki Avenis", category: "Sporty 125cc Scooter", idv: "90,000 – 1.05 Lakh", premium: "₹950 – ₹1,300" },
              { model: "Suzuki V-Strom SX 250", category: "Adventure Touring Bike", idv: "2.1 – 2.4 Lakh", premium: "₹2,000 – ₹2,600" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.model}</h3>
                <div className="text-gray-600 mb-1"><strong>Category:</strong> {item.category}</div>
                <div className="text-gray-600 mb-1"><strong>Approx. IDV:</strong> {item.idv}</div>
                <div className="text-gray-600"><strong>Starting Premium:</strong> {item.premium}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Suzuki Model</th>
                  <th className="p-3 border border-gray-300 text-left">Type</th>
                  <th className="p-3 border border-gray-300 text-left">Approx. IDV (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">Starting Premium (₹)</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["Suzuki Access 125", "125cc Scooter", "75,000 – 90,000", "₹850 – ₹1,200"],
                  ["Suzuki Burgman Street", "Premium Maxi Scooter", "95,000 – 1.10 Lakh", "₹1,100 – ₹1,400"],
                  ["Suzuki Gixxer 155", "Street Sport Motorcycle", "1.1 – 1.3 Lakh", "₹1,300 – ₹1,700"],
                  ["Suzuki Gixxer SF 250", "Full-Faired Performance", "1.6 – 1.8 Lakh", "₹1,800 – ₹2,200"],
                  ["Suzuki Avenis", "Sporty 125cc Scooter", "90,000 – 1.05 Lakh", "₹950 – ₹1,300"],
                  ["Suzuki V-Strom SX 250", "Adventure Touring Bike", "2.1 – 2.4 Lakh", "₹2,000 – ₹2,600"],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Must-Have Add-Ons for Suzuki Insurance
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          To strengthen your coverage, consider these useful add-ons for Suzuki two-wheelers:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Insurers Offering Suzuki Two-Wheeler Coverage
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          The following insurers are known for competitive Suzuki coverage and fast claims:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Bajaj Allianz</h3>
            <p className="text-gray-700 text-sm mt-1">Good for bundled dealership policies.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">HDFC ERGO</h3>
            <p className="text-gray-700 text-sm mt-1">Strong app-based claim support and fast settlement.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Tata AIG</h3>
            <p className="text-gray-700 text-sm mt-1">Excellent for sports or adventure bikes like Gixxer and V-Strom.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Digit Insurance</h3>
            <p className="text-gray-700 text-sm mt-1">Transparent pricing and app-based policy management.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📱</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">New India Assurance</h3>
            <p className="text-gray-700 text-sm mt-1">Trustworthy and government-backed.</p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                How to Save on Suzuki Two-Wheeler Insurance Premium
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Use the following strategies to keep your Suzuki insurance cost-effective:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Maintain a clean driving record to build up No Claim Bonus.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Compare plans online on platforms like TheFinMaster.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Avoid unnecessary add-ons unless you truly need them.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Opt for a voluntary deductible if you’re confident in your riding skills.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Bundle long-term policies for scooters to avoid annual renewals.</span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Needed to Buy/Renew Suzuki Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Before you purchase or renew your plan, keep these handy:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Registration Certificate (RC) of the vehicle</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Previous insurance policy (if applicable)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>PUC certificate (Pollution Under Control)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>ID proof (Aadhaar, PAN, or Driver’s License)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>Passport-sized photograph (if required by the insurer)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          FAQs about Suzuki Two-Wheeler Insurance
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Whether you commute on a Suzuki Access or go on adventures with a V-Strom, the right insurance plan shields you from financial stress and compliance issues. A tailored policy with adequate add-ons ensures your bike stays roadworthy while saving you from high repair or liability costs.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we help you compare Suzuki two-wheeler insurance options, choose from trusted providers, and enjoy a seamless, paperless experience — all in one place.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default SuzukiTwoWheelerInsurance;